import { Link } from 'react-router-dom';
import { ArrowRight, Clock3, MapPinned, Phone, Route, ShieldCheck } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_HREF, WHATSAPP_HREF } from '../../../lib/contact';
import { areaClusters, servicePages } from '../../../lib/schiek-content';

const featuredServices = servicePages.slice(0, 4);

const quickLinks = [
  { label: 'Leistungen', href: '/leistungen' },
  { label: 'Einsatzgebiete', href: '/einsatzgebiete' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Kontakt', href: '/kontakt' }
];

export default function HeroSection() {
  return (
    <section id="hero" aria-labelledby="hero-heading" className="relative overflow-hidden bg-[#06080d] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(251,191,36,0.16),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(148,163,184,0.12),transparent_28%)]" />
        <div className="absolute inset-0 opacity-[0.18] [background-image:linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:28px_28px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-24 sm:px-6 lg:px-8 lg:pb-14 lg:pt-28">
        <div className="grid gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-stretch">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-white/5 px-4 py-2 text-sm text-amber-100">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Abschleppdienst Berlin 24/7
            </div>

            <h1 id="hero-heading" className="mt-6 max-w-4xl text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl">
              Schiek ist der direkte Abschleppdienst für Berlin, wenn ein Fahrzeug sicher bewegt werden muss.
            </h1>

            <p className="mt-5 max-w-2xl text-base leading-8 text-slate-200 sm:text-lg">
              Notruf, Pannenhilfe, Unfallbergung und Falschparker auf Privatgrund. Keine Vermittlungsplattform,
              keine künstliche Hotline-Schleife, sondern klare Disposition und sichtbare Struktur.
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href={PHONE_HREF}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-300"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                Jetzt anrufen
              </a>
              <a
                href="/leistungen"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-amber-300/40 hover:bg-white/10"
              >
                Leistungen öffnen
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href={WHATSAPP_HREF}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-6 py-3 text-sm font-semibold text-emerald-100 transition-colors hover:bg-emerald-400/15"
              >
                WhatsApp
              </a>
            </div>

            <div className="mt-7 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Clock3, title: '24/7 erreichbar', text: 'Tag und Nacht, auch Wochenende und Feiertage.' },
                { icon: MapPinned, title: 'Berlin & Brandenburg', text: 'Klare regionale Ausrichtung statt leerer Schlagworte.' },
                { icon: ShieldCheck, title: 'Direkte Beauftragung', text: 'Ohne Clubstruktur, ohne Vermittlungsplattform.' }
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="rounded-3xl border border-white/10 bg-white/5 p-4 shadow-[0_16px_40px_rgba(0,0,0,0.22)]">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-amber-400/20 bg-amber-400/10 text-amber-200">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="mt-1 text-sm leading-6 text-slate-300">{item.text}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 rounded-[1.75rem] border border-white/10 bg-white/5 p-4 sm:p-5">
              <p className="text-sm font-semibold text-amber-100">Multipager direkt öffnen</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {quickLinks.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-slate-950/60 px-4 py-2 text-sm text-slate-100 transition-colors hover:border-amber-300/40 hover:bg-slate-950"
                  >
                    {item.label}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <div className="h-full rounded-[2rem] border border-white/10 bg-slate-950/88 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.46)] sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
                <div>
                  <p className="text-sm font-semibold text-amber-100">Dispositionsboard</p>
                  <p className="mt-1 text-sm text-slate-300">Berlin · Brandenburg · direkte Ansprache</p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-100">
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                  Direkt telefonisch
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {featuredServices.map((service, index) => (
                  <Link
                    key={service.slug}
                    to={`/leistungen/${service.slug}`}
                    className="group rounded-3xl border border-white/10 bg-white/5 p-4 transition-colors hover:border-amber-300/30 hover:bg-white/8"
                  >
                    <p className="text-xs text-amber-100">
                      0{index + 1} / Service
                    </p>
                    <h3 className="mt-3 text-lg font-semibold text-white">{service.shortTitle}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-300">{service.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-amber-200">
                      Öffnen
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>

              <div className="mt-5 rounded-3xl border border-amber-400/20 bg-amber-400/10 p-4">
                <p className="text-sm font-semibold text-amber-100">Telefon direkt sichtbar</p>
                <a href={PHONE_HREF} className="mt-2 inline-flex items-center gap-2 text-2xl font-semibold text-white">
                  <Phone className="h-5 w-5 text-amber-200" aria-hidden="true" />
                  {PHONE_DISPLAY}
                </a>
                <p className="mt-2 text-sm leading-6 text-slate-300">
                  Wenn Sie anrufen, hören Sie direkt, was relevant ist: Leistung, Gebiet und nächster Schritt.
                </p>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {areaClusters.slice(0, 6).map((area) => (
                  <div key={area} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                    {area}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-[#0b0f16]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-300">
            <span className="font-semibold text-white">Schnellzugriff</span>
            {quickLinks.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-2 text-sm text-slate-100 transition-colors hover:border-amber-300/30 hover:bg-white/10"
              >
                {item.label}
                <Route className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
